/**
 * Host control plane — Media Asset → Experience → Strategy → Capabilities.
 *
 * Sprint 8 Phase B3: production playback originates from a Media Asset whenever
 * practical. Legacy video_details remain the transport DTO until VMS emits
 * media_asset natively; this module normalizes once per load.
 */

import {
  mediaAssetFromVideoDetails,
  createMediaAsset,
  createCompanionImageAudio,
  requiresNativeChrome,
  inferPresentationKind,
  inferSourceType,
} from "../tpg/MediaAsset";
import { resolvePresentationStrategy } from "../tpg/PresentationStrategy";
import { resolveCapabilityModules } from "../tpg/CapabilityModules";
import {
  resolveMediaFromVideoDetails,
  resolvePlayableSrc,
} from "./buildHostPlayerConfig";
import {
  resolvePlaybackExperience,
  getPrimaryIntroLayer,
} from "../../components/Helper/playbackExperience";
import { resolveFeatureFlags } from "../Utilities/featureFlags";

/**
 * Prefer API-provided media_asset; else derive from legacy video_details.
 * @param {object} details
 * @param {object} [mediaMeta]
 * @returns {object}
 */
export function resolveMediaAssetFromDetails(details, mediaMeta) {
  details = details || {};
  mediaMeta = mediaMeta || {};
  if (details.media_asset && typeof details.media_asset === "object") {
    var fromApi = createMediaAsset(details.media_asset);
    // Companion / Image+Audio: never backfill a progressive video primary.
    var isCompanion =
      fromApi.presentationKind === "image_audio" ||
      fromApi.presentationKind === "audio" ||
      fromApi.sourceType === "companion" ||
      !!(fromApi.companion && (fromApi.companion.image || fromApi.companion.audio));
    if (
      !isCompanion &&
      (!fromApi.primary || !fromApi.primary.url) &&
      mediaMeta.src
    ) {
      fromApi.primary = {
        url: mediaMeta.src,
        role: "primary",
        mimeType: mediaMeta.type || null,
      };
    }
    if (
      (!fromApi.presentationKind ||
        fromApi.presentationKind === "unknown") &&
      mediaMeta.src
    ) {
      fromApi.presentationKind = inferPresentationKind(
        Object.assign({}, details, { primary: fromApi.primary })
      );
      fromApi.sourceType = inferSourceType(
        Object.assign({}, details, { primary: fromApi.primary })
      );
    }
    // Ensure companion exists when API omitted it but DTO has flat fields
    if (
      !fromApi.companion &&
      (details.companion_image_url || details.companion_audio_url)
    ) {
      fromApi.companion = createCompanionImageAudio({
        image: details.companion_image_url
          ? { url: details.companion_image_url, role: "image" }
          : null,
        audio: details.companion_audio_url
          ? { url: details.companion_audio_url, role: "audio" }
          : null,
        loopMode: details.loop_mode || "none",
      });
      fromApi.presentationKind = "image_audio";
      fromApi.sourceType = "companion";
    }
    return fromApi;
  }
  return mediaAssetFromVideoDetails(details, mediaMeta);
}

/**
 * Map capability module ids → NextGenPlayer features bag (BC layer).
 * @param {string[]} enabledIds
 * @param {object} baseFeatures
 * @returns {object}
 */
export function featuresFromCapabilityModules(enabledIds, baseFeatures) {
  var ids = enabledIds || [];
  var has = function (id) {
    return ids.indexOf(id) !== -1;
  };
  var f = Object.assign({}, baseFeatures || {});
  f.subtitles = has("subtitles");
  f.audioTracks = has("audioTracks");
  f.playbackSpeed = has("playbackSpeed");
  f.chapters = has("chapters");
  f.continueWatching = has("resume") || !!f.continueWatching;
  f.theaterMode = has("theater");
  f.fullscreen = has("fullscreen");
  f.brandedLoading = has("brandedLoading") || !!f.brandedLoading;
  f.watermarks = has("watermark");
  f.chromecast = has("chromecast");
  f.airplay = has("airplay");
  f.casting = has("chromecast") || has("airplay") || !!f.casting;
  f.analytics = has("analytics");
  f.drm = has("drm");
  f.avod = has("avod");
  f.companionPresentation = has("companion");
  f.live = has("live");
  f.dvr = has("dvr");
  f.playlist = has("playlist");
  f.channel = has("channel");
  f.highContrast = has("accessibility") ? !!f.highContrast : !!f.highContrast;
  return f;
}

/**
 * Build the unified host control plane for one playback request.
 *
 * @param {object} details  video_details / V4 DTO
 * @param {object} [opts]
 * @param {string} [opts.videoFrom]
 * @param {boolean} [opts.isTrailer]
 * @param {object} [opts.featureOverrides]
 * @returns {object}
 */
export function resolveHostControlPlane(details, opts) {
  opts = opts || {};
  details = details || {};
  var isTrailer = !!opts.isTrailer || opts.videoFrom === "trailer";
  var mediaMeta = resolveMediaFromVideoDetails(details, {
    videoFrom: opts.videoFrom,
  });
  var mediaAsset = resolveMediaAssetFromDetails(details, mediaMeta);
  var strategy = resolvePresentationStrategy(mediaAsset);
  var isCompanionSurface =
    (strategy && strategy.usesCompanionImage) ||
    (mediaAsset &&
      (mediaAsset.presentationKind === "image_audio" ||
        mediaAsset.presentationKind === "audio" ||
        mediaAsset.sourceType === "companion"));

  // Keep mediaMeta.src aligned with Media Asset primary so host gates
  // (hasPlayableSrc) and buildHostPlayerConfig share one playable URL.
  // Companion presentations must never feed an image URL into progressive video.
  var playable = isCompanionSurface
    ? ""
    : resolvePlayableSrc(mediaMeta, mediaAsset);
  if (isCompanionSurface) {
    mediaMeta = Object.assign({}, mediaMeta, { src: "" });
  } else if (playable && mediaMeta.src !== playable) {
    mediaMeta = Object.assign({}, mediaMeta, { src: playable });
  }
  if (
    !isCompanionSurface &&
    playable &&
    mediaAsset &&
    (!mediaAsset.primary || !mediaAsset.primary.url)
  ) {
    mediaAsset = Object.assign({}, mediaAsset, {
      primary: {
        url: playable,
        role: "primary",
        mimeType: (mediaMeta && mediaMeta.type) || null,
      },
    });
  }
  var experience = resolvePlaybackExperience(details, { isTrailer: isTrailer });
  var introLayer = getPrimaryIntroLayer(experience);

  var baseFlags = resolveFeatureFlags(
    Object.assign({}, opts.featureOverrides || {})
  );
  // Seed site defaults from host config path (caller may pass site features)
  if (opts.siteFeatures) {
    baseFlags = resolveFeatureFlags(
      Object.assign({}, opts.siteFeatures, opts.featureOverrides || {})
    );
  }

  var enabledCapabilities = resolveCapabilityModules({
    featureFlags: baseFlags,
    strategy: strategy,
    asset: mediaAsset,
    licenseeAllowlist: opts.licenseeAllowlist || null,
  });

  var features = featuresFromCapabilityModules(
    enabledCapabilities,
    Object.assign({}, opts.siteFeatures || {}, opts.featureOverrides || {})
  );

  return {
    mediaAsset: mediaAsset,
    mediaMeta: mediaMeta,
    strategy: strategy,
    experience: experience,
    introLayer: introLayer,
    enabledCapabilities: enabledCapabilities,
    features: features,
    requiresNativeChrome: requiresNativeChrome(mediaAsset),
    showHostTitle: !requiresNativeChrome(mediaAsset),
    isTrailer: isTrailer,
  };
}

/**
 * Cache key for control plane + plugin set (invalidate on content / intro mode).
 * @param {object} plane
 * @param {object} [extra]
 * @returns {string}
 */
export function controlPlaneCacheKey(plane, extra) {
  extra = extra || {};
  var asset = (plane && plane.mediaAsset) || {};
  return [
    asset.id || "",
    asset.presentationKind || "",
    (plane && plane.strategy && plane.strategy.id) || "",
    extra.showingIntro ? "1" : "0",
    extra.isTrailer ? "1" : "0",
    extra.sessionId || "",
    (plane && plane.enabledCapabilities
      ? plane.enabledCapabilities.slice().sort().join(",")
      : ""),
  ].join("|");
}

export default resolveHostControlPlane;
